import { get, post } from 'api/client';
import config from 'config';
import { CancelToken } from 'axios';
import { mapMentionSearchResponse } from 'api/mentions/mapMentionSearchResponse';
import type {
  MentionSearchEnvelope,
  MentionSearchResponse,
} from 'api/mentions/types';

interface SuggestionsParams {
  pageId: number;
  query: string;
  cancelToken?: CancelToken;
}

/**
 * Matches the endpoint's `entity_name` rules (`required`, `max:255`). Both
 * violations 422 and are swallowed by `reportSelection`'s `.catch(() => {})`,
 * so an unguarded blank or over-long name would silently drop the report
 * with no signal anywhere. Blank names are reachable today: the Instagram
 * location pickers render `value.name ? value.name : t('unknownLocation')`
 * for the currently selected value, which only makes sense if picking a
 * blank-named option is possible in the first place.
 */
const MAX_REPORTED_ENTITY_NAME_LENGTH = 255;
const FALLBACK_ENTITY_NAME = '(unnamed)';

const sanitizeReportedEntityName = (name: string): string =>
  (name || FALLBACK_ENTITY_NAME).slice(0, MAX_REPORTED_ENTITY_NAME_LENGTH);

export const SuggestionApi = {
  /**
   * One URL, slug or name -> at most one candidate, in the same envelope
   * `mentions` returns. Replaces the legacy monolith's
   * `POST /pages/ajaxRemoteSearchForFBPage`, which was Facebook-only and
   * answered with a bare object that could not distinguish "no such page"
   * from "your connection is dead".
   *
   * No `min:3` on the server, unlike search: a resolve is one deliberate
   * submit where a two-letter vanity slug is a legitimate query.
   */
  resolveMention(params: SuggestionsParams): Promise<MentionSearchEnvelope> {
    return get<MentionSearchResponse>('mentions/resolve', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: { page_id: params.pageId, q: params.query },
      cancelToken: params.cancelToken,
    }).then(mapMentionSearchResponse);
  },
  /**
   * Facebook places the Instagram composer can offer as a location tag.
   *
   * Same envelope as `mentions` and `resolveMention`, so the failure matrix and
   * the mapping are shared rather than reinvented — and unlike the retired
   * endpoint this replaced, a dead connection is distinguishable from "no place
   * matches" instead of both rendering as nothing.
   */
  locations(params: SuggestionsParams): Promise<MentionSearchEnvelope> {
    return get<MentionSearchResponse>('mentions/locations', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: { page_id: params.pageId, q: params.query },
      cancelToken: params.cancelToken,
    }).then(mapMentionSearchResponse);
  },
  mentions(params: SuggestionsParams): Promise<MentionSearchEnvelope> {
    return get<MentionSearchResponse>('mentions/search', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: { page_id: params.pageId, q: params.query },
      cancelToken: params.cancelToken,
    }).then(mapMentionSearchResponse);
  },
  /**
   * Best-effort telemetry for R7 (which mention targets get picked).
   *
   * Deliberately returns `void` and swallows everything: the caller runs on
   * the mention-insertion path, and a failed or slow report must never delay
   * or block inserting the mention. The server answers 204 for storage
   * failures too, so a rejection here means the request never arrived.
   */
  reportSelection(params: {
    pageId: number;
    entityKind: 'company' | 'person' | 'page' | 'location';
    entityId: string;
    entityName: string;
    source: 'search' | 'resolve' | 'location_picker';
  }): void {
    void post(
      'mentions/selections',
      {
        page_id: params.pageId,
        entity_kind: params.entityKind,
        entity_id: params.entityId,
        entity_name: sanitizeReportedEntityName(params.entityName),
        source: params.source,
      },
      { baseURL: config.API_SERVICE_ENDPOINT },
    ).catch(() => undefined);
  },
};
